import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from "@mui/material";
import {
  ArchiveOutlined as ArchiveIcon,
  UnarchiveOutlined as UnarchiveIcon,
  MergeType
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { DataGrid } from "../../../components/datagrid";
import { ArchiveConfirmDialog } from "../../../components/archiveConfirmDialog";
import {
  CounterpartyStatusFilter,
  useArchiveCounterparty,
  useCounterpartyOverview,
  useRestoreCounterparty
} from "../../tradingCounterparties/dataHooks/counterpartyQueries";
import {
  useArchiveBroker,
  useBrokerOverview,
  useRestoreBroker
} from "../../brokers/dataHooks/brokerQueries";
import { EntityKind } from "../../../utils/entityPaths";

type AdminMergerRow = {
  rowKey: string;
  kind: EntityKind;
  id: string;
  name: string;
  tradingTeam?: string;
  contactPersonTradingTeam?: string;
  archived?: boolean;
  archivedAt?: string | null;
  archivedBy?: string | null;
};

type PendingAction = {
  mode: "archive" | "restore";
  kind: EntityKind;
  id: string;
  name: string;
};

const KIND_LABELS: Record<EntityKind, string> = {
  counterparty: "Counterparty",
  broker: "Broker"
};

/**
 * Admin > Mergers > List: archive and restore mergers. A merger is a counterparty or broker, so
 * this reads both books' overviews filtered to mergers (the Counterparties and Brokers lists
 * leave them out) and archives through the matching book's endpoint. Mergers have no trader
 * access or onboarding request tabs of their own: grants still live under Counterparties and
 * Brokers, and mergers are never requested.
 */
export const AdminMergers: FC = () => {
  const [filter, setFilter] = useState<CounterpartyStatusFilter>("active");
  const [pending, setPending] = useState<PendingAction | null>(null);

  const counterparties = useCounterpartyOverview(filter, "only");
  const brokers = useBrokerOverview(filter, "only");

  const archiveCounterparty = useArchiveCounterparty();
  const restoreCounterparty = useRestoreCounterparty();
  const archiveBroker = useArchiveBroker();
  const restoreBroker = useRestoreBroker();

  // Counterparty and broker ids come from different books, so rows key on kind plus id.
  const rows = useMemo<AdminMergerRow[]>(
    () => [
      ...(counterparties.data ?? [])
        .filter((s) => s.id)
        .map((s) => ({
          rowKey: `counterparty:${s.id}`,
          kind: "counterparty" as const,
          id: s.id!,
          name: s.counterpartyName ?? "",
          tradingTeam: s.tradingTeam,
          contactPersonTradingTeam: s.contactPersonTradingTeam,
          archived: s.archived,
          archivedAt: s.archivedAt,
          archivedBy: s.archivedBy
        })),
      ...(brokers.data ?? [])
        .filter((s) => s.id)
        .map((s) => ({
          rowKey: `broker:${s.id}`,
          kind: "broker" as const,
          id: s.id!,
          name: s.brokerName ?? "",
          tradingTeam: s.tradingTeam,
          contactPersonTradingTeam: s.contactPersonTradingTeam,
          archived: s.archived,
          archivedAt: s.archivedAt,
          archivedBy: s.archivedBy
        }))
    ],
    [counterparties.data, brokers.data]
  );

  const columns = useMemo<GridColDef<AdminMergerRow>[]>(() => {
    const base: GridColDef<AdminMergerRow>[] = [
      {
        field: "name",
        headerName: "Merger",
        minWidth: 240,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MergeType fontSize="small" />
            Merger
          </Box>
        )
      },
      {
        field: "kind",
        headerName: "Type",
        width: 140,
        type: "singleSelect",
        valueOptions: Object.values(KIND_LABELS),
        valueGetter: (_v, row) => KIND_LABELS[row.kind],
        renderCell: (params) => (
          <Chip size="small" variant="outlined" label={params.value} />
        )
      },
      {
        field: "tradingTeam",
        headerName: "Trading Team",
        width: 200
      },
      {
        field: "contactPersonTradingTeam",
        headerName: "MFT Contact Person",
        width: 240
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        valueGetter: (_v, row) => (row.archived ? "Archived" : "Active")
      }
    ];

    const archiveAuditColumns: GridColDef<AdminMergerRow>[] = [
      {
        field: "archivedBy",
        headerName: "Archived by",
        width: 220
      },
      {
        field: "archivedAt",
        headerName: "Archived at",
        width: 220,
        valueGetter: (_v, row) =>
          row.archivedAt ? new Date(row.archivedAt) : null,
        valueFormatter: (value: Date | null) =>
          value ? dayjs(value).format("LLL") : ""
      }
    ];

    const actionsColumn: GridColDef<AdminMergerRow> = {
      field: "actions",
      headerName: "",
      width: 140,
      sortable: false,
      filterable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const { id, kind, name, archived } = params.row;
        const isArchived = archived === true;
        return (
          <Tooltip title={isArchived ? "Restore" : "Archive"}>
            <Button
              size="small"
              variant="outlined"
              color={isArchived ? "primary" : "error"}
              startIcon={
                isArchived ? (
                  <UnarchiveIcon fontSize="small" />
                ) : (
                  <ArchiveIcon fontSize="small" />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                setPending({
                  mode: isArchived ? "restore" : "archive",
                  kind,
                  id,
                  name: name || "this merger"
                });
              }}
            >
              {isArchived ? "Restore" : "Archive"}
            </Button>
          </Tooltip>
        );
      }
    };

    return filter === "active"
      ? [...base, actionsColumn]
      : [...base, ...archiveAuditColumns, actionsColumn];
  }, [filter]);

  const handleConfirm = () => {
    if (!pending) return;
    const mutation =
      pending.kind === "counterparty"
        ? pending.mode === "archive"
          ? archiveCounterparty
          : restoreCounterparty
        : pending.mode === "archive"
          ? archiveBroker
          : restoreBroker;
    mutation.mutate(pending.id, {
      onSettled: () => setPending(null)
    });
  };

  const loading =
    counterparties.isLoading ||
    counterparties.isFetching ||
    brokers.isLoading ||
    brokers.isFetching ||
    archiveCounterparty.isPending ||
    restoreCounterparty.isPending ||
    archiveBroker.isPending ||
    restoreBroker.isPending;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, next: CounterpartyStatusFilter | null) =>
            next && setFilter(next)
          }
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="archived">Archived</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          loading={loading}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.rowKey}
          disableRowSelectionOnClick
        />
      </Box>

      <ArchiveConfirmDialog
        open={pending !== null}
        mode={pending?.mode ?? "archive"}
        entityLabel="merger"
        entityName={pending?.name ?? ""}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirm}
      />
    </Box>
  );
};
